import type {
  GetOrganizationCaseCountsRequest,
  GetOrganizationCaseCountsResponse,
  OutputFieldCondition,
  SearchField,
} from '@compass/client/types/case_record';
import { useQuery } from '@tanstack/react-query';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';
import { caseRecordService } from '@/services/api';
import { useStore } from '@/store/index';

/**
 * One quick-filter tab's count inputs. Mirrors the `QuickFilter` shape consumed by
 * {@link import('./useCaseCounts').useCaseCounts} but for the org-wide page.
 */
export interface OrgCountsQuickFilter {
  name: string;
  /** Tab hides the date picker; its count uses `pinnedDates` instead of the picker range. */
  hidesDatePicker?: boolean;
  filter: {
    states?: string[];
    /** PQC submitted-substate predicates — counted via a dedicated total_count call. */
    outputConditions?: OutputFieldCondition[];
  };
}

/**
 * Org-wide scope + shared filters for the counts request. The scope mirrors what
 * `useOrgCases` builds: specific manufacturers → `workflow_ids`, otherwise all PQC
 * via `workflow_types`. Counts honor manufacturer/assignee/date AND the active
 * search (Case-ID exact lookup or free-text Atlas search) so badge totals track
 * the visible list — matching the per-program page's `useCaseCounts`.
 */
export interface OrgCountsScope {
  workflowIds?: string[];
  workflowTypes?: string[];
  assignedUserIds?: string[];
  createdAtAfter?: string;
  createdAtBefore?: string;
  // Search filters — included so badge counts reflect the active search.
  caseRecordIds?: string[];
  caseCodes?: string[];
  referenceIds?: string[];
  searchField?: SearchField;
  searchValue?: string;
}

// Non-empty array → itself, else undefined (so optional request fields stay unset).
function nonEmpty<T>(arr?: T[]): T[] | undefined {
  return arr && arr.length > 0 ? arr : undefined;
}

const buildRequest = (
  organizationId: string,
  scope: OrgCountsScope,
  statuses: string[],
  groupByStatus: boolean,
  outputConditions?: OutputFieldCondition[],
): GetOrganizationCaseCountsRequest => ({
  organization_id: organizationId,
  workflow_ids: nonEmpty(scope.workflowIds),
  workflow_types: nonEmpty(scope.workflowTypes),
  assigned_user_ids: nonEmpty(scope.assignedUserIds),
  created_at_after: scope.createdAtAfter,
  created_at_before: scope.createdAtBefore,
  case_record_ids: nonEmpty(scope.caseRecordIds),
  case_codes: nonEmpty(scope.caseCodes),
  reference_ids: nonEmpty(scope.referenceIds),
  search_field: scope.searchField,
  search_value: scope.searchValue || undefined,
  status: nonEmpty(statuses),
  group_by_status: groupByStatus || undefined,
  output_conditions: nonEmpty(outputConditions),
});

/**
 * Org-wide per-tab quick-filter counts for the Tier-2 cases page. Mirrors
 * {@link import('./useCaseCounts').useCaseCounts}'s batch/merge strategy but calls
 * the org-scoped `GetOrganizationCaseCounts` (no program/workflow id — scoped to the
 * caller's accessible programs by the backend):
 *  - state-based tabs are batched into one `group_by_status` call, then fanned out
 *    to the tabs that declare each status;
 *  - output-condition tabs (PQC submitted-substates) each get a dedicated
 *    `total_count` call (status + output_conditions can't be distinguished by group).
 * Tabs that hide the date picker (the work queues) count on `pinnedDates`
 * (the last 12 months) instead of the picker range. Grouped calls are
 * deduped per distinct range (AQ-863).
 */
export const useOrgCaseCounts = (
  quickFilters: OrgCountsQuickFilter[],
  scope: OrgCountsScope,
  pinnedDates?: Pick<OrgCountsScope, 'createdAtAfter' | 'createdAtBefore'>,
) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: [
      'compass',
      'tier2',
      'org-counts',
      selectedOrganizationId || '',
      scope,
      pinnedDates,
      quickFilters,
    ],
    enabled: !!selectedOrganizationId,
    queryFn: async (): Promise<Record<string, number>> => {
      const organizationId = selectedOrganizationId!;
      const scopeFor = (f: OrgCountsQuickFilter): OrgCountsScope =>
        f.hidesDatePicker && pinnedDates ? { ...scope, ...pinnedDates } : scope;

      const outputTabs = quickFilters.filter(f => !!f.filter.outputConditions?.length);
      const stateTabs = quickFilters.filter(f => !f.filter.outputConditions?.length);

      // Initialize every tab to 0 so badges show a value, not blank.
      const countsMap: Record<string, number> = {};
      quickFilters.forEach(f => {
        countsMap[f.name] = 0;
      });

      // One grouped call per distinct date range; fan each response out to the
      // tabs that declare each status. Status-less tabs (e.g. "All") use total_count.
      const groupedByScope = new Map<
        string,
        { scope: OrgCountsScope; tabs: OrgCountsQuickFilter[] }
      >();
      stateTabs.forEach(f => {
        const s = scopeFor(f);
        const key = JSON.stringify(s);
        (
          groupedByScope.get(key) ?? groupedByScope.set(key, { scope: s, tabs: [] }).get(key)!
        ).tabs.push(f);
      });
      const groupedResponses = await Promise.all(
        [...groupedByScope.values()].map(async ({ scope: groupScope, tabs }) => {
          const response: GetOrganizationCaseCountsResponse =
            await caseRecordService.getOrganizationCaseCounts(
              buildRequest(
                organizationId,
                groupScope,
                [...new Set(tabs.flatMap(f => f.filter.states ?? []))],
                true,
              ),
            );
          return { response, tabs };
        }),
      );
      for (const { response, tabs: groupedTabs } of groupedResponses) {
        const statusToFilterNames: Record<string, string[]> = {};
        groupedTabs.forEach(f => {
          (f.filter.states ?? []).forEach(s => {
            (statusToFilterNames[s] ??= []).push(f.name);
          });
        });
        (response.groups ?? []).forEach(group => {
          const names = group.status ? statusToFilterNames[group.status] : undefined;
          if (names) {
            const c = group.count || 0;
            names.forEach(n => {
              countsMap[n] = (countsMap[n] || 0) + c;
            });
          }
        });
        groupedTabs.forEach(f => {
          if (!f.filter.states || f.filter.states.length === 0) {
            countsMap[f.name] = response.total_count ?? 0;
          }
        });
      }

      // Output-condition tabs: one dedicated total_count call each.
      if (outputTabs.length) {
        const results = await Promise.all(
          outputTabs.map(async tab => {
            const resp = await caseRecordService.getOrganizationCaseCounts(
              buildRequest(
                organizationId,
                scopeFor(tab),
                tab.filter.states ?? [],
                false,
                tab.filter.outputConditions,
              ),
            );
            return { name: tab.name, count: resp.total_count ?? 0 };
          }),
        );
        results.forEach(({ name, count }) => {
          countsMap[name] = count;
        });
      }

      return countsMap;
    },
    ...CASES_QUERY_CONFIG,
  });

  return { counts: query.data ?? {}, isLoading: query.isLoading };
};
